import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  Container,
  SearchBarSection,
  SearchBarSectionSubcontainer,
  SummarySection,
} from "./styles";
import SearchBar from "../../components/ui-v2/Inputs/Search";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import DropdownFilter from "../../components/ui-v2/DropdownFilter";
import {
  /* Table , */ TCellNotTagMiscellaneous,
  TCellStyleVariants,
  TTableBody,
} from "../../components/ui-v2/TableGrid";
import {
  breakStringPreserveSpaces,
  cnpjMask,
  cnpjMaskedPreview,
  cpfMask,
  cpfMaskedPreview,
} from "../../utils/formatters";

import {
  TInvoiceEntryStatus,
  setIsLoadingIssuedInvoices,
  setIsRestoringQuerystring,
  setToastState,
} from "../../redux/reducers/globalState";
import { useTypedDispatch } from "../../hooks/useTypedDispatch";
import {
  fetchIssuedInvoices,
} from "../../redux/actions";
import { useNavigationManager } from "../../hooks/useNavigationManager";
import {
  CALENDAR_CONTENT_OPTIONS,
  TCalendarMenuOptions,
} from "../../components/ui-v2/DropdownFilter/Menu";
import { sub } from "date-fns/sub";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import {
  handleParseToDateCalendarOptions,
  handleRevertTranslateCalendarOptions,
  handleSetEndDate,
  handleTranslateCalendarOptions,
} from "../../utils/miscellaneous";
import TableGrid from "../../components/ui-v2/TableGrid";
import { ROUTES } from "../../router";
import { UilCopyAlt, UilExclamationCircle } from "@iconscout/react-unicons";
import axios, { CancelTokenSource } from "axios";
import Button from "../../components/ui-v2/Button";
import { useProportionalGridTemplateColumns } from "../../hooks/useProportionalGridTemplateColumns";
import {
  invoiceStatusOptions,
  natureOptions,
  TIssuedInvoice,
  TStatus,
} from "./mock";
import Tag from "../../components/ui-v2/Tag";
import Tooltip from "../../components/ui-v2/Tooltip";
import Text from "../../components/ui-v2/Text";

// De-para entre o rótulo exibido e o inteiro esperado pelo backend
// (issued_invoices.models.IssuedInvoice.EmissionStatus / Nature).
const STATUS_LABEL_TO_VALUE: Record<string, number> = {
  "Em processamento": 10,
  Autorizada: 20,
  "Autorizada em contingência": 30,
  Rejeitada: 40,
  Denegada: 50,
  Cancelada: 60,
  Inutilizada: 70,
};

const NATURE_LABEL_TO_VALUE: Record<string, number> = {
  "001 - Compra": 1,
  "002 - Venda": 2,
  "003 - Transferência": 3,
  "004 - Devolução": 4,
  "005 - Importação": 5,
  "006 - Exportação": 6,
  "007 - Remessa": 7,
  "008 - Retorno": 8,
  "009 - Outras entradas": 9,
  "010 - Outras saídas": 10,
  "011 - Serviços - ISSQN": 11,
  "012 - Lançamento de Crédito": 12,
  "013 - Recebimento": 13,
  "014 - Ressarcimento": 14,
  "015 - Serviço - ICMS": 15,
};

// O backend retorna xml_url/pdf_url como caminho RELATIVO (ex.: /media/...).
// Aberto a partir do front (localhost:5173) isso resolveria para o próprio
// front e cairia em 404/redirect. Prefixa com VITE_BASE_URL (a API) quando a
// URL for relativa. Retorna "" quando não há arquivo.
const resolveFileUrl = (url?: string | null): string => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url; // já absoluta
  const base = (import.meta.env.VITE_BASE_URL ?? "").replace(/\/+$/, "");
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${base}${path}`;
};

function IssuedInvoices() {
  const dispatch = useTypedDispatch();
  const { goTo, setQuerystring } = useNavigationManager();

  const isRestoringQuerystring = useTypedSelector(
    (state) => state.globalState.isRestoringQuerystring
  );

  const fetchInvoicesCancelTokenRef = useRef<CancelTokenSource>(
    axios.CancelToken.source()
  );

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const { issuedInvoiceState: invoiceState } = useTypedSelector(
    (state) => state.globalState.data
  );

  const isLoading = useTypedSelector(
    (state) => state.globalState.isLoadingIssuedInvoices
  );

  const sidebarState = useTypedSelector((state) => state.sidebar);

  const reduxQuerystring = useTypedSelector((state) => state.querystring);

  const selectedOrganization = useTypedSelector(
    (state) => state.organizations.selectedOrganization
  );

  const startDateRef = useRef<string | undefined>(
    format(sub(new Date(), { days: 30 }), "yyyy-MM-dd")
  );
  const endDateRef = useRef<string | undefined>(
    format(new Date(), "yyyy-MM-dd")
  );

  const entryStatusRef = useRef<TInvoiceEntryStatus | undefined>(
    reduxQuerystring.launch_status === 40
      ? undefined
      : (reduxQuerystring.launch_status as TInvoiceEntryStatus)
  );

  const payablesRef = useRef<boolean | undefined>();
  const goodsEntryRef = useRef<boolean | undefined>();
  const pageRef = useRef(1);
  const itemsPerPageRef = useRef(100);
  const searchRef = useRef<string | undefined>();

  // Seleções atuais de Natureza e Status (rótulos selecionados).
  const natureFilterRef = useRef<string[]>([]);
  const statusFilterRef = useRef<string[]>([]);
  const natureSetSelectedRef = useRef<React.Dispatch<
    React.SetStateAction<string[]>
  > | null>(null);
  const statusSetSelectedRef = useRef<React.Dispatch<
    React.SetStateAction<string[]>
  > | null>(null);

  const allInvoicesRef = useRef<TIssuedInvoice[]>([]);

  const searchBarContainerId = useId();

  const [entryStatusSearch, setEntryStatusSearch] = useState("");

  const [shouldUpdateComponent, _setShouldUpdateComponent] = useState(false);

  const [shouldRejectFilledEntryStatus /* setShouldRejectFilledEntryStatus */] =
    useState(false);

  const [visibleDocumentNumbers, setVisibleDocumentNumbers] = useState<
    Record<string, boolean>
  >({});

  const [visibleRejectReasons, setVisibleRejectReasons] = useState<
    Record<string, boolean>
  >({});

  // Filter options based on search
  const filteredNatureOptions = natureOptions.filter((option) =>
    option.toLowerCase().includes(entryStatusSearch.toLowerCase())
  );

  // For filtering with search:
  const filteredInvoiceStatusOptions = invoiceStatusOptions.filter((option) =>
    option.toLowerCase().includes(entryStatusSearch.toLowerCase())
  );

  const originalWidths = [12, 42, 74, 88, 179, 130, 124, 194, 194, 203, 125, 0];
  const sidebarOffset = sidebarState.isFixed /* && sidebarState.isOpen */
    ? 240
    : 48;

  const gridTemplateColumns = useProportionalGridTemplateColumns(
    originalWidths,
    1440
  );

  const toggleDocumentNumberVisibility = (uuid: string) => {
    setVisibleDocumentNumbers((prev) => ({
      ...prev,
      [uuid]: !prev[uuid],
    }));
  };

  const toggleRejectReasonVisibility = (uuid: string) => {
    setVisibleRejectReasons((prev) => ({
      ...prev,
      [uuid]: !prev[uuid],
    }));
  };

  const handleFormatStatusCell = (status: TStatus | null, uuid: string) => {
    switch (status) {
      case "Em processamento":
        return (
          <Tag
            color="blue"
            icon="clock"
            iconPosition="left"
            size="medium"
            text={status}
          />
        );
      case "Autorizada":
        return (
          <Tag
            color="green"
            icon="checkCircle"
            iconPosition="left"
            size="medium"
            text={status}
          />
        );
      case "Autorizada em contingência":
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Tag
              color="green"
              icon="checkCircle"
              iconPosition="left"
              size="medium"
              text={status.replace(" em contingência", "")}
            />
            <Tooltip
              text={[
                {
                  text: "Em contingência",
                  shouldBreakLine: false,
                  style: "default",
                },
              ]}
              size="large"
            >
              <div
                style={{
                  padding: "4px",
                  borderRadius: "100%",
                  backgroundColor: "var(--color-aux-warning-100)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <UilExclamationCircle
                  style={{
                    width: 14,
                    height: 14,
                    color: "var(--color-aux-warning-800)",
                  }}
                />
              </div>
            </Tooltip>
          </div>
        );
      case "Rejeitada":
        return (
          <Tag
            color="red"
            icon="exclamationTriangle"
            iconPosition="left"
            size="medium"
            text={status}
            onClick={() => {
              toggleRejectReasonVisibility(uuid);
            }}
          />
        );
      case "Denegada":
        return (
          <Tag
            color="red"
            icon="exclamationTriangle"
            iconPosition="left"
            size="medium"
            text={status}
            onClick={() => {
              toggleRejectReasonVisibility(uuid);
            }}
          />
        );
      case "Cancelada":
        return (
          <Tag
            color="darkGray"
            icon="timesCircle"
            iconPosition="left"
            size="medium"
            text={status}
          />
        );
      case "Inutilizada":
        return (
          <Tag
            color="orange"
            icon="trash"
            iconPosition="left"
            size="medium"
            text={status}
          />
        );
      case null:
        return { style: "default", value: " - " };
    }
  };

  const rowFormatter = (invoice: TIssuedInvoice, index: number) => {
    return [
      { style: "default", value: invoice?.model ?? "-" },
      { style: "default", value: invoice?.number ? invoice.number : "-" },
      {
        style: "default",
        value: invoice.issue_date
          ? format(invoice?.issue_date, "dd/MM/yyyy 'às' HH:mm")
          : "-",
      },
      {
        style: "default",
        value: invoice.amount
          ? "R$ " + `${invoice?.amount}`.replace(".", ",")
          : "-",
      },
      {
        style: "link",
        value: invoice?.order?.number ?? "-",
        url: invoice?.order?.url ?? "",
      },
      { style: "default", value: invoice?.nature ?? "-" },
      invoice?.recipient?.name ? (
        <div
          className="recipient-data"
          style={{ display: "flex", gap: 8, alignItems: "flex-end" }}
        >
          <Tooltip
            text={(() => {
              const values = breakStringPreserveSpaces(
                invoice.recipient.name,
                24
              );

              return values.map((value, index) => ({
                style: "default",
                text: value,
                shouldBreakLine: index !== values.length - 1,
              }));
            })()}
          >
            <>
              <Text
                style="bold"
                text={
                  invoice.recipient.name
                    ? invoice?.recipient.name.length > 20
                      ? invoice.recipient.name.slice(0, 20) + "..."
                      : invoice.recipient.name
                    : "-"
                }
                shouldBreakLine
              />
              <Text
                style="default"
                text={(() => {
                  const document = (
                    invoice.recipient.document_number ?? ""
                  ).replace(/[./-]/g, "");

                  let value = "-";

                  const isCNPJ = document.length === 14;

                  if (isCNPJ) {
                    if (visibleDocumentNumbers[invoice.uuid] && document) {
                      value = cnpjMask(document);
                    } else if (document) {
                      value = cnpjMaskedPreview(document);
                    }
                  } else {
                    if (visibleDocumentNumbers[invoice.uuid] && document) {
                      value = cpfMask(document);
                    } else if (document) {
                      value = cpfMaskedPreview(document);
                    }
                  }

                  return value;
                })()}
                shouldBreakLine={false}
              />
            </>
          </Tooltip>
          <Tooltip
            text={(() => {
              const value = visibleDocumentNumbers[invoice.uuid]
                ? "ocultar"
                : "mostrar";

              return [
                {
                  style: "default",
                  text: value,
                  shouldBreakLine: false,
                },
              ];
            })()}
          >
            <>
              <Button
                icon="onlyIcon"
                iconType={
                  visibleDocumentNumbers[invoice.uuid] ? "eyeSlash" : "eye"
                }
                variant="onlyIcon"
                onClick={() => {
                  toggleDocumentNumberVisibility(invoice.uuid);
                }}
              />
            </>
          </Tooltip>
        </div>
      ) : (
        { style: "faded", value: "Não identificado" }
      ),

      handleFormatStatusCell(invoice.status, invoice.uuid),

      [
        {
          style: "button",
          icon: "nfe",
          isDisabled: !invoice.pdf_url,
          onClick() {
            const u = resolveFileUrl(invoice.pdf_url);
            if (u) window.open(u, "_blank");
          },
          tooltip: {
            text: [
              invoice.pdf_url
                ? {
                    style: "default",
                    text: "Visualizar DANFE",
                    shouldBreakLine: false,
                  }
                : {
                    style: "default",
                    text: "DANFE indisponível",
                    shouldBreakLine: false,
                  },
            ],
            customTopPosition: index * 97 + 223,
            hasScrollableRef: true,
          },
        },
        {
          style: "button",
          icon: "xml",
          isDisabled: !invoice.xml_url,
          onClick() {
            const u = resolveFileUrl(invoice.xml_url);
            if (u) window.open(u, "_blank");
          },
          tooltip: {
            text: [
              invoice.xml_url
                ? {
                    style: "default",
                    text: "Visualizar XML",
                    shouldBreakLine: false,
                  }
                : {
                    style: "default",
                    text: "XML indisponível",
                    shouldBreakLine: false,
                  },
            ],
            customTopPosition: index * 97 + 223,
            hasScrollableRef: true,
          },
        },
      ],
    ] as TTableBody<TCellStyleVariants>[];
  };

  const handleSecondRowLeftSideCustomNodeRender = (
    invoice: TIssuedInvoice,
    _index: number
  ) => {
    return (
      <span>
        <Tooltip
          text={[{ text: "Copiar", style: "default", shouldBreakLine: false }]}
        >
          <button
            role="button"
            onClick={() => {
              navigator.clipboard.writeText(invoice.access_key).then(() => {
                dispatch(
                  setToastState({
                    variant: "success",
                    message: {
                      title: "Sucesso",
                      description: "Chave de acesso copiada",
                    },
                    shouldShow: true,
                  })
                );
              });
            }}
          >
            <UilCopyAlt />
          </button>
        </Tooltip>
      </span>
    );
  };

  const secondRowFormatter = (invoice: TIssuedInvoice) => ({
    style: "miscellaneous",
    values: [
      {
        style: "default",
        text: "Chave de acesso: ",
        shouldBreakLine: false,
      },
      {
        style: "faded",
        text: invoice.access_key,
        shouldBreakLine: false,
      },
    ],
  });

  const handleCalendarOnClear = () => {
    const querystring = { ...reduxQuerystring };

    startDateRef.current = undefined;
    endDateRef.current = undefined;
    delete querystring.start_date;
    delete querystring.end_date;
    querystring.period = "all";

    if (querystring.page) {
      querystring.page = 1;
    }

    if (querystring.modal) {
      delete querystring.modal;
    }

    setQuerystring(querystring);

    debouncedUpdateAllInvoicesRef();
  };

  const handleCalendarOnSelect = (selected: string[]) => {
    const select = selected as TCalendarMenuOptions[];
    const querystring = { ...reduxQuerystring };
    if (CALENDAR_CONTENT_OPTIONS.includes(select[0])) {
      const tempStartDate = handleParseToDateCalendarOptions(select[0]);

      if (tempStartDate === undefined) {
        startDateRef.current = undefined;
        endDateRef.current = undefined;
        delete querystring.start_date;
        delete querystring.end_date;
        querystring.period = "all";

        if (querystring.page) {
          querystring.page = 1;
        }

        if (querystring.modal) {
          delete querystring.modal;
        }

        setQuerystring(querystring);

        setTimeout(() => {
          debouncedUpdateAllInvoicesRef();
        }, 100);
      } else {
        const formatTempStartDate = format(tempStartDate, "yyyy-MM-dd");
        if (formatTempStartDate !== startDateRef.current) {
          const formatEndDate = handleSetEndDate(select[0]);
          startDateRef.current = formatTempStartDate;
          endDateRef.current = formatEndDate;
          querystring.period = handleTranslateCalendarOptions(
            select[0] as TCalendarMenuOptions
          ) as string;
          delete querystring.start_date;
          delete querystring.end_date;

          if (querystring.page) {
            querystring.page = 1;
          }

          if (querystring.modal) {
            delete querystring.modal;
          }

          setQuerystring(querystring);

          setTimeout(() => {
            debouncedUpdateAllInvoicesRef(undefined, formatEndDate);
          }, 100);
        }
      }
    } else {
      const dates = select[0].split(" - ");
      const parsedStart =
        dates[0] && typeof dates[0] === "string"
          ? parse(dates[0], "dd/MM/yyyy", new Date())
          : undefined;
      const tempStartDate =
        parsedStart && !isNaN(parsedStart.getTime())
          ? format(parsedStart, "yyyy-MM-dd")
          : "";

      const parsedEnd =
        dates[1] && typeof dates[1] === "string"
          ? parse(dates[1], "dd/MM/yyyy", new Date())
          : undefined;
      const tempEndDate =
        parsedEnd && !isNaN(parsedEnd.getTime())
          ? format(parsedEnd, "yyyy-MM-dd")
          : "";

      if (tempStartDate !== startDateRef.current)
        startDateRef.current = tempStartDate;
      if (tempEndDate !== endDateRef.current) endDateRef.current = tempEndDate;
      querystring.end_date = tempEndDate;
      querystring.start_date = tempStartDate;
      querystring.period = undefined;

      if (querystring.page) {
        querystring.page = 1;
      }

      if (querystring.modal) {
        delete querystring.modal;
      }

      setQuerystring(querystring);

      setTimeout(() => {
        debouncedUpdateAllInvoicesRef();
      }, 100);
    }
  };

  const handlePeriodForceSelectOptions = () => {
    const queryStrings = { ...reduxQuerystring };

    const calendarOptions = [
      ...CALENDAR_CONTENT_OPTIONS.map((string) => string.toLowerCase()),
    ];

    if (
      queryStrings.period &&
      calendarOptions.includes(
        handleRevertTranslateCalendarOptions(
          queryStrings.period as TCalendarMenuOptions
        ) as string
      )
    ) {
      return [
        calendarOptions.indexOf(
          handleRevertTranslateCalendarOptions(
            queryStrings.period as TCalendarMenuOptions
          ) as string
        ),
      ];
    } else if (queryStrings.start_date && queryStrings.end_date) {
      startDateRef.current = queryStrings.start_date;
      endDateRef.current = queryStrings.end_date;

      return [
        queryStrings.start_date.replace(/(\d{4})-(\d{2})-(\d{2})/, "$3/$2/$1"),
        queryStrings.end_date.replace(/(\d{4})-(\d{2})-(\d{2})/, "$3/$2/$1"),
      ];
    }

    return undefined;
  };

  // ===========================================================================
  // Filtros reais de Natureza e Status (emission_status) — gravam no querystring
  // os inteiros esperados pelo backend e refazem o fetch.
  //
  // Os refs guardam os RÓTULOS selecionados (não índices), porque o índice que o
  // DropdownFilter entrega é relativo à lista JÁ filtrada pela busca interna.
  // ===========================================================================

  const handleNatureCustomFilter = (
    index: string,
    _selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
    selectDataRef: React.MutableRefObject<string[]>,
    prevSelectDataRef: React.MutableRefObject<string[]>,
    setShouldUpdate: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    natureSetSelectedRef.current = setSelected;
    const label = filteredNatureOptions[Number(index)];
    if (!label) return;

    const current = natureFilterRef.current;
    prevSelectDataRef.current = [...current];
    const next = current.includes(label)
      ? current.filter((v) => v !== label)
      : [...current, label];
    natureFilterRef.current = Array.from(new Set(next));

    // Reflete a seleção visual (índices sobre a lista completa).
    const indexes = natureFilterRef.current
      .map((l) => natureOptions.indexOf(l))
      .filter((i) => i >= 0)
      .map(String);
    selectDataRef.current = indexes;
    setSelected(indexes);
    setShouldUpdate(true);
    setTimeout(() => setShouldUpdate(false), 20);
  };

  const handleStatusCustomFilter = (
    index: string,
    _selected: string[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
    selectDataRef: React.MutableRefObject<string[]>,
    prevSelectDataRef: React.MutableRefObject<string[]>,
    setShouldUpdate: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    statusSetSelectedRef.current = setSelected;
    const label = filteredInvoiceStatusOptions[Number(index)];
    if (!label) return;

    const current = statusFilterRef.current;
    prevSelectDataRef.current = [...current];
    const next = current.includes(label)
      ? current.filter((v) => v !== label)
      : [...current, label];
    statusFilterRef.current = Array.from(new Set(next));

    const indexes = statusFilterRef.current
      .map((l) => invoiceStatusOptions.indexOf(l))
      .filter((i) => i >= 0)
      .map(String);
    selectDataRef.current = indexes;
    setSelected(indexes);
    setShouldUpdate(true);
    setTimeout(() => setShouldUpdate(false), 20);
  };

  // Converte os rótulos selecionados em inteiros do backend e dispara o fetch.
  const applyNatureFilter = () => {
    const queryStrings = { ...reduxQuerystring };
    const values = natureFilterRef.current
      .map((label) => NATURE_LABEL_TO_VALUE[label])
      .filter((v) => v !== undefined);

    if (values.length) {
      queryStrings.nature = values;
    } else {
      delete queryStrings.nature;
    }

    if (queryStrings.page) queryStrings.page = 1;
    if (queryStrings.modal) delete queryStrings.modal;

    setQuerystring(queryStrings);
    debouncedUpdateAllInvoicesRef();
  };

  const applyStatusFilter = () => {
    const queryStrings = { ...reduxQuerystring };
    const values = statusFilterRef.current
      .map((label) => STATUS_LABEL_TO_VALUE[label])
      .filter((v) => v !== undefined);

    if (values.length) {
      queryStrings.status = values;
    } else {
      delete queryStrings.status;
    }

    if (queryStrings.page) queryStrings.page = 1;
    if (queryStrings.modal) delete queryStrings.modal;

    setQuerystring(queryStrings);
    debouncedUpdateAllInvoicesRef();
  };

  const clearNatureFilter = () => {
    natureFilterRef.current = [];
    const queryStrings = { ...reduxQuerystring };
    delete queryStrings.nature;
    if (queryStrings.page) queryStrings.page = 1;
    setQuerystring(queryStrings);
    debouncedUpdateAllInvoicesRef();
  };

  const clearStatusFilter = () => {
    statusFilterRef.current = [];
    const queryStrings = { ...reduxQuerystring };
    delete queryStrings.status;
    if (queryStrings.page) queryStrings.page = 1;
    setQuerystring(queryStrings);
    debouncedUpdateAllInvoicesRef();
  };

  // Reidrata as seleções a partir do querystring (índices sobre a lista completa).
  const natureForceSelectOptions = () => {
    const selected = reduxQuerystring.nature;
    if (!selected || selected.length === 0) return undefined;
    const labels = selected
      .map((value) =>
        Object.keys(NATURE_LABEL_TO_VALUE).find(
          (k) => NATURE_LABEL_TO_VALUE[k] === value
        )
      )
      .filter((l): l is string => !!l);
    natureFilterRef.current = labels;
    const indexes = labels
      .map((l) => natureOptions.indexOf(l))
      .filter((i) => i >= 0);
    return indexes;
  };

  const statusForceSelectOptions = () => {
    const selected = reduxQuerystring.status;
    if (!selected || selected.length === 0) return undefined;
    const labels = selected
      .map((value) =>
        Object.keys(STATUS_LABEL_TO_VALUE).find(
          (k) => STATUS_LABEL_TO_VALUE[k] === value
        )
      )
      .filter((l): l is string => !!l);
    statusFilterRef.current = labels;
    const indexes = labels
      .map((l) => invoiceStatusOptions.indexOf(l))
      .filter((i) => i >= 0);
    return indexes;
  };

  const updateAllInvoicesRef = useCallback(
    (_entryStatus?: number, _endDate?: string) => {
      const queryStringParams = { ...reduxQuerystring };
      if (!queryStringParams.modal) {
        endDateRef.current = queryStringParams.end_date;
        startDateRef.current = queryStringParams.start_date;
        payablesRef.current = queryStringParams.has_payable_accounts;
        goodsEntryRef.current = queryStringParams.has_goods_receipt;
        pageRef.current = queryStringParams.page;
        itemsPerPageRef.current = queryStringParams.items_per_page;
        entryStatusRef.current = queryStringParams.launch_status as
          | TInvoiceEntryStatus
          | undefined;

        // Cancel previous request
        if (fetchInvoicesCancelTokenRef.current) {
          fetchInvoicesCancelTokenRef.current.cancel(
            "Operation canceled due to new request."
          );
        }
        // Create new token for this request
        fetchInvoicesCancelTokenRef.current = axios.CancelToken.source();

        dispatch(
          fetchIssuedInvoices({
            cancelToken: (
              fetchInvoicesCancelTokenRef.current as CancelTokenSource
            ).token,
          })
        )
          .unwrap()
          .then((response) => {
            allInvoicesRef.current = response?.results;
          })
          .catch((e) => {
            if (axios.isCancel(e)) {
              // Request was canceled, do nothing
            } else {
              console.log(e);
            }
          });
      }
    },
    [window.location.search, reduxQuerystring]
  );

  const debouncedUpdateAllInvoicesRef = useCallback(
    (_entryStatus?: number | undefined, _endDate?: string | undefined) => {
      const queryStringParams = { ...reduxQuerystring };
      if (!queryStringParams.modal && !isRestoringQuerystring) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        dispatch(setIsLoadingIssuedInvoices(true));
        debounceRef.current = setTimeout(() => {
          updateAllInvoicesRef(_entryStatus, _endDate);
        }, 500);
      }

      if (isRestoringQuerystring) {
        dispatch(setIsRestoringQuerystring(false));
      }
    },
    [updateAllInvoicesRef]
  );

  useEffect(() => {
    debouncedUpdateAllInvoicesRef();
  }, [debouncedUpdateAllInvoicesRef]);

  useEffect(() => {
    if (window.location.pathname === "/") {
      goTo(ROUTES.INVOICES, reduxQuerystring);
    }
  }, [window.location.pathname]);

  return (
    <Container>
      <SummarySection>
        <div>
          <div>
            <h1>Notas emitidas</h1>
            <p>
              <b>CNPJ:&nbsp;</b>
              {selectedOrganization && selectedOrganization?.cnpj
                ? cnpjMask(selectedOrganization.cnpj)
                : ""}
            </p>
          </div>
          {/* {invoicesUpdatedAt.responseStatus !== 204 &&
            invoicesUpdatedAt.date.length > 0 && (
              <p>
                Atualizado em {format(invoicesUpdatedAt.date, "dd/MM/yyyy")} -{" "}
                {format(invoicesUpdatedAt.date, "HH:mm")}
              </p>
            )} */}
        </div>
        <div className="buttons-container">
          {/* <Button
            icon="plus"
            iconPosition="left"
            variant="outline"
            text="Importar"
            onClick={() => {
              if (filesInputRef.current) filesInputRef.current.click();
            }}
          /> */}
          <div></div>
          <Button
            icon="export"
            iconPosition="left"
            variant="secondary"
            style={{ padding: "12px 16px" }}
            text="Exportar notas"
            onClick={() => {
              goTo(ROUTES.EXPORT_ISSUED, { ...reduxQuerystring });
            }}
          />
        </div>
      </SummarySection>
      <SearchBarSection>
        <SearchBar
          key={reduxQuerystring.invoices_search}
          onClear={() => {
            searchRef.current = undefined;
            const queryStrings = { ...reduxQuerystring };

            delete queryStrings.invoices_search;

            if (entryStatusRef.current) {
              queryStrings.launch_status = entryStatusRef.current ?? 40;
            }

            setQuerystring(queryStrings);

            dispatch(
              fetchIssuedInvoices({
                cancelToken: (
                  fetchInvoicesCancelTokenRef.current as CancelTokenSource
                ).token,
              })
            );
          }}
          $ref={(ref, setSearchString) => {
            if (reduxQuerystring.invoices_search && ref.current) {
              const search = decodeURIComponent(
                reduxQuerystring.invoices_search
              ).replace(/\+/g, " ");
              ref.current.value = search;
              setSearchString(search);
            }
          }}
          onSearch={(searchPhrase) => {
            searchRef.current = searchPhrase;

            if (selectedOrganization) {
              const queryStrings = { ...reduxQuerystring };

              if (searchRef.current.length > 0) {
                queryStrings.invoices_search = searchRef.current;
              } else {
                delete queryStrings.invoices_search;
              }

              if (entryStatusRef.current) {
                queryStrings.launch_status = entryStatusRef.current ?? 40;
              }

              if (queryStrings.page) {
                queryStrings.page = 1;
              }

              if (queryStrings.modal) {
                delete queryStrings.modal;
              }

              setQuerystring(queryStrings);

              // Cancel previous request before dispatching a new one
              if (fetchInvoicesCancelTokenRef.current) {
                fetchInvoicesCancelTokenRef.current.cancel(
                  "Operation canceled due to new request."
                );
              }
              fetchInvoicesCancelTokenRef.current = axios.CancelToken.source();

              dispatch(
                fetchIssuedInvoices({
                  cancelToken: fetchInvoicesCancelTokenRef.current.token,
                })
              );
            }
          }}
          variant="default"
          placeholder="Buscar nota"
        />
        <SearchBarSectionSubcontainer id={searchBarContainerId}>
          <DropdownFilter
            key={
              reduxQuerystring.period +
              "" +
              reduxQuerystring.start_date +
              reduxQuerystring.end_date
            }
            buttonVariant="calendar"
            menuVariant="calendar"
            onSelect={handleCalendarOnSelect}
            onClear={handleCalendarOnClear}
            forceSelectOptionsByIndex={(() => {
              const result = handlePeriodForceSelectOptions();
              return shouldRejectFilledEntryStatus ||
                (result && typeof result[0] === "string")
                ? []
                : (result as number[]);
            })()}
            filled={(() => {
              const result = handlePeriodForceSelectOptions();

              return result === undefined
                ? result
                : typeof result[0] === "string"
                ? (result as string[])
                : [CALENDAR_CONTENT_OPTIONS[result[0]]];
            })()}
          />
          <DropdownFilter
            key={"nature-" + (reduxQuerystring.nature?.join(",") ?? "none")}
            buttonVariant="infoCircle"
            placeholder="Natureza"
            content={filteredNatureOptions}
            menuVariant="unlabeled"
            search={{
              placeholder: "pesquisar",
              onChange: (searchPhrase) => {
                setEntryStatusSearch(searchPhrase);
              },
              onClear() {
                setEntryStatusSearch("");
              },
            }}
            customFilter={handleNatureCustomFilter}
            onAction={applyNatureFilter}
            onClear={clearNatureFilter}
            onCancel={() => {
              const reset = natureForceSelectOptions()?.map(String) ?? [];
              natureFilterRef.current = reset;
              setTimeout(() => {
                if (natureSetSelectedRef.current) {
                  natureSetSelectedRef.current(reset);
                }
              }, 200);
            }}
            hasBottomBar
            forceSelectOptionsByIndex={(() => {
              const result = natureForceSelectOptions();
              return shouldRejectFilledEntryStatus ? undefined : result;
            })()}
            filled={(() => {
              const result = natureForceSelectOptions();
              return result === undefined ? result : result.map(String);
            })()}
          />
          <DropdownFilter
            key={"status-" + (reduxQuerystring.status?.join(",") ?? "none")}
            buttonVariant="checkCircle"
            placeholder="Status"
            content={filteredInvoiceStatusOptions}
            menuVariant="unlabeled"
            search={{
              placeholder: "pesquisar",
              onChange: (searchPhrase) => {
                setEntryStatusSearch(searchPhrase);
              },
              onClear() {
                setEntryStatusSearch("");
              },
            }}
            customFilter={handleStatusCustomFilter}
            onAction={applyStatusFilter}
            onClear={clearStatusFilter}
            onCancel={() => {
              const reset = statusForceSelectOptions()?.map(String) ?? [];
              statusFilterRef.current = reset;
              setTimeout(() => {
                if (statusSetSelectedRef.current) {
                  statusSetSelectedRef.current(reset);
                }
              }, 200);
            }}
            hasBottomBar
            forceSelectOptionsByIndex={(() => {
              const result = statusForceSelectOptions();
              return shouldRejectFilledEntryStatus ? undefined : result;
            })()}
            filled={(() => {
              const result = statusForceSelectOptions();
              return result === undefined ? result : result.map(String);
            })()}
          />
        </SearchBarSectionSubcontainer>
      </SearchBarSection>

      {invoiceState && (
        <TableGrid
          key={shouldUpdateComponent + "-invoices-tablegrid"}
          row="tripleRow"
          columns={{
            tableHead: {
              // 22px 77px 160px 342px 356px 185px 346px 400px 400px 0px
              width: [gridTemplateColumns],
            },
            tableBody: {
              width: [gridTemplateColumns],
            },
          }}
          onAction={() => {
            //
          }}
          emptyState={{
            isEmptyState: !isLoading && invoiceState?.results.length === 0,
            title: "Nenhuma nota encontrada",
            description: "Altere os filtros e realize a busca novamente",
          }}
          secondRow={{
            params: invoiceState?.results.map((invoice) => ({
              data: {
                style: "miscellaneous",
                values: [
                  { style: "default", text: "", shouldBreakLine: false },
                ],
              },
              customLinkContainer:
                invoice.status === "Rejeitada" || invoice.status === "Denegada"
                  ? {
                      hasDoubleRow: !!visibleRejectReasons[invoice.uuid],
                      node:
                        visibleRejectReasons[invoice.uuid] &&
                        (invoice.rejection || invoice.denegation) ? (
                          <div style={{ fontSize: "12px", marginLeft: "15px" }}>
                            <b style={{ color: "var(--color-aux-danger-800)" }}>
                              Motivo:
                            </b>
                            &nbsp;
                            {
                              (invoice.rejection || invoice.denegation).code
                            } -{" "}
                            {(invoice.rejection || invoice.denegation).reason}
                          </div>
                        ) : (
                          <></>
                        ),
                      trCSSProperties: {
                        background: "var(--color-aux-danger-100)",
                        display: "flex",
                        borderBottom: "1px solid var(--color-aux-danger-700)",
                      },
                    }
                  : { hasDoubleRow: false },
            })),
          }}
          thirdRow={{
            params: invoiceState?.results.map((invoice, index) => ({
              data: {
                ...(secondRowFormatter(invoice) as TCellNotTagMiscellaneous),
                skeleton: isLoading
                  ? {
                      containerCss: {
                        display: "flex",
                        justifyContent: "space-between",
                        width: "calc(100vw - 155px)",
                        margin: "0 20px",
                      },
                      values: [
                        { width: "216px", height: "16px" },
                        {
                          width: "96px",
                          height: "16px",
                          style: { maxWidth: "96px", overflow: "hidden" },
                        },
                      ],
                    }
                  : undefined,
              },
              leftSideCustomNode: !isLoading && (
                <>{handleSecondRowLeftSideCustomNodeRender(invoice, index)}</>
              ),
            })),
          }}
          customMarginTop="27px"
          selectType={{ type: "multiSelect", fullData: allInvoicesRef.current }}
          tableHead={{
            customPaddingTop: "0px",
            value: [
              { style: "noIcon", showSkeleton: isLoading, value: "Modelo" },
              { style: "noIcon", showSkeleton: isLoading, value: "Número" },
              {
                style: "noIcon",
                showSkeleton: isLoading,
                value: "Data emissão",
              },
              { style: "noIcon", showSkeleton: isLoading, value: "Valor" },
              { style: "noIcon", showSkeleton: isLoading, value: "Pedido" },
              { style: "noIcon", showSkeleton: isLoading, value: "Natureza" },
              {
                style: "noIcon",
                showSkeleton: isLoading,
                value: "Destinatário",
              },
              { style: "noIcon", showSkeleton: isLoading, value: "Status" },
              { style: "noIcon", showSkeleton: isLoading, value: "Arquivos" },
            ],
          }}
          isLoading={isLoading}
          tableBody={
            isLoading
              ? new Array(5).fill("").map(() => ({
                  elementId: "",
                  file_nfe: null,
                  file_xml: null,
                  body: [
                    {
                      style: "default",
                      value: "",
                      skeleton: { values: [{ width: "50px" }] },
                    },
                    {
                      style: "default",
                      value: "",
                      skeleton: { values: [{ width: "50px" }] },
                    },
                    {
                      style: "default",
                      value: "",
                      skeleton: { values: [{ width: "120px" }] },
                    },
                    {
                      style: "default",
                      value: "",
                      skeleton: { values: [{ width: "88px" }] },
                    },
                    {
                      style: "default",
                      value: "",
                      skeleton: { values: [{ width: "88px" }] },
                    },
                    {
                      style: "default",
                      value: "",
                      skeleton: { values: [{ width: "120px" }] },
                    },
                    {
                      style: "default",
                      value: "",
                      skeleton: { values: [{ width: "130px" }] },
                    },
                    {
                      style: "default",
                      value: "",
                      skeleton: { values: [{ width: "150px" }] },
                    },
                    {
                      style: "default",
                      value: "",
                      skeleton: {
                        containerCss: { display: "flex", gap: "4px" },
                        values: [
                          {
                            width: "40px",
                            height: "40px",
                            variant: "circle",
                          },
                          {
                            width: "40px",
                            height: "40px",
                            variant: "circle",
                          },
                        ],
                      },
                    },
                  ] as TTableBody<TCellStyleVariants>[],
                }))
              : (invoiceState?.results ?? []).map(
                  (invoice: TIssuedInvoice, index: number) => ({
                    elementId: invoice.uuid,
                    file_nfe: invoice.pdf_url,
                    file_xml: invoice.xml_url,
                    body: rowFormatter(invoice, index),
                  })
                )
          }
          footer={{
            customStyle: {
              width: `calc(100vw - 48px - ${sidebarOffset}px)`,
            },
            leftSideDescription:
              " | Total de " +
              invoiceState.count +
              (invoiceState.count > 1
                ? " registros encontrados"
                : " registro encontrado"),

            onSelectItemsPerPage(amount) {
              const querystring = { ...reduxQuerystring };

              if (querystring.page) {
                pageRef.current = querystring.page;
              }

              if (pageRef.current) querystring.page = pageRef.current;

              querystring.items_per_page = amount;

              setQuerystring(querystring);

              dispatch(
                fetchIssuedInvoices({
                  cancelToken: fetchInvoicesCancelTokenRef.current.token,
                })
              );
            },
            customPagination: invoiceState
              ? {
                  fixedBottom: {
                    isFixedAtTheBottom: true,
                    customBottomPosition: "0px",
                  },
                  currentPage: `${invoiceState?.current_page}`,
                  buttonBackward: {
                    disabled: invoiceState.current_page === 1,
                    onClick() {
                      pageRef.current = invoiceState.current_page - 1;

                      const queryStrings = { ...reduxQuerystring };

                      if (queryStrings.items_per_page) {
                        itemsPerPageRef.current = queryStrings.items_per_page;
                      }

                      if (invoiceState.current_page !== 1) {
                        if (pageRef.current)
                          queryStrings.page = pageRef.current;

                        setQuerystring(queryStrings);

                        dispatch(
                          fetchIssuedInvoices({
                            cancelToken:
                              fetchInvoicesCancelTokenRef.current.token,
                          })
                        );
                      }
                    },
                  },
                  buttonForward: {
                    disabled:
                      invoiceState.current_page === invoiceState.pages.length,
                    onClick() {
                      if (
                        invoiceState.current_page === invoiceState.pages.length
                      ) {
                        pageRef.current = invoiceState.current_page;
                      } else {
                        pageRef.current = invoiceState.current_page + 1;
                      }

                      const queryStrings = { ...reduxQuerystring };

                      if (queryStrings.items_per_page) {
                        itemsPerPageRef.current = queryStrings.items_per_page;
                      }

                      if (
                        invoiceState.current_page !== invoiceState.pages.length
                      ) {
                        if (pageRef.current)
                          queryStrings.page = pageRef.current;
                        setQuerystring(queryStrings);

                        dispatch(
                          fetchIssuedInvoices({
                            cancelToken:
                              fetchInvoicesCancelTokenRef.current.token,
                          })
                        );
                      }
                    },
                  },
                  pages: invoiceState.pages.map((page) => {
                    return {
                      value: Object.keys(page)[0],
                      onClick() {
                        let temp =
                          Object.values(page)[0].match(/.*?page=(\d+)&per_/);

                        if (temp !== null && temp[1]) {
                          pageRef.current = parseInt(temp[1]);
                        } else {
                          pageRef.current = 1;
                        }

                        const queryStrings = { ...reduxQuerystring };
                        if (queryStrings.items_per_page) {
                          itemsPerPageRef.current = queryStrings.items_per_page;
                        }

                        if (pageRef.current)
                          queryStrings.page = pageRef.current;
                        setQuerystring(queryStrings);

                        dispatch(
                          fetchIssuedInvoices({
                            cancelToken:
                              fetchInvoicesCancelTokenRef.current.token,
                          })
                        );
                      },
                    };
                  }),
                }
              : undefined,
          }}
        />
      )}
    </Container>
  );
}

export default IssuedInvoices;
